import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import { fetchDistributorBestOffers } from '../../../services/Http/Api';
import { formatDiscountBadge } from '../../../utils/discountDisplay';

const DistributorBestOffers = () => {
    const { t } = useTranslation();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetchDistributorBestOffers();
                setOffers(response.data || []);
            } catch (error) {
                toast.error(t('DistributorBestOffers.error'));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [t]);

    if (loading) {
        return <Placeholder>{t('common.loading')}</Placeholder>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('DistributorBestOffers.title')}</PageTitle>
                <Subtitle>{t('DistributorBestOffers.subtitle')}</Subtitle>
            </PageHeader>
            <PageContent>
                {offers.length === 0 ? (
                    <Placeholder>{t('DistributorBestOffers.empty')}</Placeholder>
                ) : (
                    <OffersGrid>
                        {offers.map((offer) => (
                            <OfferCard key={offer.id}>
                                {offer.image_url ? (
                                    <Image src={offer.image_url} alt={offer.product_name} />
                                ) : (
                                    <ImagePlaceholder>{offer.product_name.charAt(0)}</ImagePlaceholder>
                                )}
                                <Content>
                                    <ProductName>{offer.product_name}</ProductName>
                                    <Meta>
                                        {offer.manufacturer_name && <span>{offer.manufacturer_name}</span>}
                                        {offer.owner_name && offer.manufacturer_name !== offer.owner_name && (
                                            <>
                                                <span>•</span>
                                                <span>{offer.owner_name}</span>
                                            </>
                                        )}
                                    </Meta>
                                    {offer.venue_name && <Badge>{offer.venue_name}</Badge>}
                                </Content>
                                <Discount>
                                    <DiscountValue>-{formatDiscountBadge(offer)}</DiscountValue>
                                    <DiscountDates>
                                        {t('DistributorBestOffers.validUntil', {
                                            end: new Intl.DateTimeFormat().format(new Date(offer.end_at)),
                                        })}
                                    </DiscountDates>
                                </Discount>
                            </OfferCard>
                        ))}
                    </OffersGrid>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default DistributorBestOffers;

const Subtitle = styled.p`
    margin: 0;
    color: #777;
`;

const OffersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
`;

const OfferCard = styled.div`
    border: 2px solid rgba(130, 22, 146, 0.2);
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: #fff;
    box-shadow: 0 10px 30px rgba(130, 22, 146, 0.08);
    transition: transform 0.2s ease, border-color 0.2s ease;

    &:hover {
        transform: translateY(-4px);
        border-color: rgba(130, 22, 146, 0.6);
    }
`;

const Image = styled.img`
    width: 100%;
    height: 180px;
    object-fit: cover;
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: 180px;
    background: rgba(130, 22, 146, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: rgba(130, 22, 146, 1);
`;

const Content = styled.div`
    padding: 1.2rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`;

const ProductName = styled.h3`
    margin: 0;
`;

const Meta = styled.div`
    display: flex;
    gap: 0.5rem;
    color: #777;
    font-size: 0.875rem;
    flex-wrap: wrap;
`;

const Badge = styled.span`
    align-self: flex-start;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.875rem;
`;

const Discount = styled.div`
    border-top: 1px solid rgba(130, 22, 146, 0.1);
    padding: 1rem 1.2rem;
    background: rgba(130, 22, 146, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
`;

const DiscountValue = styled.span`
    font-size: 1.6rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 700;
`;

const DiscountDates = styled.span`
    color: #555;
    font-size: 0.875rem;
`;

const Placeholder = styled.div`
    padding: 2.5rem;
    text-align: center;
    border: 1px dashed rgba(130, 22, 146, 0.3);
    border-radius: 12px;
    color: #777;
`;
